import { useQuery } from "@tanstack/react-query";
import { useSyncExternalStore, type ReactNode } from "react";
import { Layout } from "./components/Layout";
import { LoginPage } from "./components/LoginPage";
import { RequiredPasswordChangePage } from "./components/RequiredPasswordChangePage";
import { RetryButton, StatePanel } from "./components/StatePanel";
import { api, ApiProblem, setAccessToken } from "./lib/api";
import type { Me } from "./lib/types";
import { AssignmentsPage } from "./pages/AssignmentsPage";
import { OverviewPage } from "./pages/OverviewPage";
import { ModerationPage } from "./pages/ModerationPage";
import { InspectionsPage } from "./pages/InspectionsPage";
import { PoliciesPage } from "./pages/PoliciesPage";
import { RequestsPage } from "./pages/RequestsPage";
import { ReviewsPage } from "./pages/ReviewsPage";
import {
  AuditPage,
  NotificationsPage,
  PeoplePage,
  PlantingsPage,
  ReportsPage,
} from "./pages/UtilityPages";

export function App() {
  const path = usePathname();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api<Me>("/me"),
    retry: (failureCount, error) =>
      failureCount < 1 &&
      (!(error instanceof ApiProblem) || error.problem.status >= 500),
  });
  if (me.isLoading) {
    return (
      <main className="centered-state">
        <StatePanel
          kind="loading"
          title="Opening BalikTANiman"
          detail="Confirming your staff identity and assigned program scope."
        />
      </main>
    );
  }
  if (me.isError || me.data === undefined) {
    if (
      me.error instanceof ApiProblem &&
      me.error.problem.code === "CREDENTIAL_ROTATION_REQUIRED"
    ) {
      return (
        <RequiredPasswordChangePage
          onCompleted={() => void me.refetch()}
          onSignedOut={() => void me.refetch()}
        />
      );
    }
    if (me.error instanceof ApiProblem && me.error.problem.status === 401) {
      setAccessToken(undefined);
      return <LoginPage onSignedIn={() => void me.refetch()} />;
    }
    return (
      <main className="centered-state">
        <StatePanel
          kind="permission"
          title="Staff access could not be confirmed"
          detail="BalikTANiman could not load your staff profile or assigned scope. Try again or contact a program administrator."
          action={<RetryButton onClick={() => void me.refetch()} />}
        />
      </main>
    );
  }
  return (
    <Layout me={me.data} path={path} onSignedOut={() => void me.refetch()}>
      {pageFor(path)}
    </Layout>
  );
}

const pages: Readonly<Record<string, ReactNode>> = {
  "/": <OverviewPage />,
  "/requests": <RequestsPage />,
  "/assignments": <AssignmentsPage />,
  "/reviews": <ReviewsPage />,
  "/inspections": <InspectionsPage />,
  "/plantings": <PlantingsPage />,
  "/reports": <ReportsPage />,
  "/policies": <PoliciesPage />,
  "/people": <PeoplePage />,
  "/audit": <AuditPage />,
  "/notifications": <NotificationsPage />,
  "/moderation": <ModerationPage />,
};

function pageFor(path: string): ReactNode {
  return pages[path] ?? <OverviewPage />;
}

function usePathname(): string {
  return useSyncExternalStore(
    (onChange) => {
      window.addEventListener("popstate", onChange);
      return () => window.removeEventListener("popstate", onChange);
    },
    () => window.location.pathname.replace(/\/+$/, "") || "/",
    () => "/",
  );
}
